import React from 'react';
import { Link } from 'react-router-dom';

const percentUsed = (used, limit) => {
  const current = Number(used) || 0;
  if (limit == null) return current > 0 ? 8 : 0;
  const max = Number(limit);
  if (!Number.isFinite(max) || max <= 0) return current > 0 ? 100 : 0;
  return Math.min(100, Math.round((current / max) * 100));
};

export const buildPlanUsageRows = (limits = {}, usage = {}) => [
  {
    key: 'bookings',
    label: 'Bookings this month',
    used: usage.bookings_this_month ?? 0,
    limit: limits.max_bookings_per_month,
  },
  {
    key: 'services',
    label: 'Services',
    used: usage.services_count ?? 0,
    limit: limits.max_services,
  },
  {
    key: 'portfolio',
    label: 'Portfolio items',
    used: usage.portfolio_count ?? 0,
    limit: limits.max_portfolio_items,
  },
  {
    key: 'images',
    label: 'Images',
    used: usage.images_count ?? 0,
    limit: limits.max_images,
  },
  {
    key: 'packages',
    label: 'Packages on a service',
    used: usage.packages_on_busiest_service ?? 0,
    limit: limits.max_packages_per_service,
  },
  ...(limits.feature_ai_generator
    ? [
        {
          key: 'ai',
          label: 'AI writes this month',
          used: usage.ai_generations_this_month ?? 0,
          limit: limits.max_ai_generations_per_month,
        },
      ]
    : []),
];

const PlanUsageMeters = ({
  planName,
  limits,
  usage,
  changePlanTo,
  changePlanLabel = 'Change plan',
  className = '',
}) => {
  const rows = buildPlanUsageRows(limits, usage);

  return (
    <section
      className={`rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5 ${className}`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-50">
          Plan usage
          {planName ? (
            <>
              {' '}
              — <span className="text-primary-600 dark:text-primary-400">{planName}</span>
            </>
          ) : null}
        </h3>
        {changePlanTo ? (
          <Link
            to={changePlanTo}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
          >
            {changePlanLabel}
          </Link>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((row) => {
          const pct = percentUsed(row.used, row.limit);
          const atLimit = row.limit != null && Number(row.used) >= Number(row.limit);
          const limitLabel = row.limit == null ? 'Unlimited' : row.limit;
          return (
            <div
              key={row.key}
              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 px-3.5 py-3"
            >
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700 dark:text-gray-200">{row.label}</span>
                <span className="font-medium tabular-nums text-gray-900 dark:text-gray-50">
                  {row.used} / {limitLabel}
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    atLimit ? 'bg-amber-500' : 'bg-primary-600 dark:bg-primary-400'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PlanUsageMeters;
